import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Alert,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Add, Search, AccountTree, ViewList, Refresh as RefreshIcon, Print as PrintIcon, FileDownload as ExcelIcon } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';
import { categoriesService } from '../../services/categories.service';
import { Category } from '../../types/categories.types';
import CategoryDialog from '../../components/categories/CategoryDialog';
import CategoryTree from '../../components/categories/CategoryTree';
import { useConfirm } from '../../hooks/useConfirm';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import config from '../../config';

const translations = {
  en: {
    title: 'Categories Management',
    addCategory: 'Add Category',
    search: 'Search categories...',
    loading: 'Loading categories...',
    error: 'Failed to load categories',
    noCategories: 'No categories found',
    deleteConfirm: 'Are you sure you want to delete this category?',
    treeView: 'Tree View',
    listView: 'List View',
    total: 'Total Categories',
    code: 'Code',
    name: 'Name',
    nameAr: 'Name (AR)',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    printList: 'Print List',
    exportExcel: 'Export Excel',
  },
  ar: {
    title: 'إدارة التصنيفات',
    addCategory: 'إضافة تصنيف',
    search: 'بحث عن التصنيفات...',
    loading: 'جاري تحميل التصنيفات...',
    error: 'فشل تحميل التصنيفات',
    noCategories: 'لا توجد تصنيفات',
    deleteConfirm: 'هل أنت متأكد من حذف هذا التصنيف؟',
    treeView: 'عرض شجري',
    listView: 'عرض قائمة',
    total: 'إجمالي التصنيفات',
    code: 'الرمز',
    name: 'الاسم',
    nameAr: 'الاسم (عربي)',
    status: 'الحالة',
    active: 'نشط',
    inactive: 'غير نشط',
    printList: 'طباعة القائمة',
    exportExcel: 'تصدير إكسل',
  },
};

const CategoriesPage: React.FC = () => {
  const { language } = useLanguage();
  const { mode } = useTheme();
  const t = translations[language];
  const isRTL = language === 'ar';
  const confirm = useConfirm();

  const [categories, setCategories] = useState<Category[]>([]);
  const [filteredCategories, setFilteredCategories] = useState<Category[]>([]);
  const [treeCategories, setTreeCategories] = useState<Category[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [viewMode, setViewMode] = useState<'tree' | 'list'>('tree');
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);

  useEffect(() => {
    loadCategories();
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
  }, []);

  useEffect(() => {
    if (searchQuery.trim() === '') {
      setFilteredCategories(categories);
      setTreeCategories(categoriesService.buildTree(categories));
    } else {
      const query = searchQuery.toLowerCase();
      const filtered = categories.filter(
        (cat) =>
          cat.categoryCode.toLowerCase().includes(query) ||
          cat.categoryName.toLowerCase().includes(query) ||
          cat.categoryNameAr.toLowerCase().includes(query)
      );
      setFilteredCategories(filtered);
      setTreeCategories(categoriesService.buildTree(filtered));
    }
  }, [searchQuery, categories]);

  const loadCategories = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await categoriesService.getAll();
      setCategories(data);
      setFilteredCategories(data);
      setTreeCategories(categoriesService.buildTree(data));
    } catch (err) {
      setError(t.error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (category: Category) => {
    const ok = await confirm({ message: t.deleteConfirm, severity: 'error' });
    if (!ok) return;
    try {
      await categoriesService.delete(category.categoryId);
      loadCategories();
    } catch (err) {
      toast.error(t.error);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingCategory(null);
  };

  const handleDialogSave = () => {
    loadCategories();
  };

  const handleAddClick = () => {
    setEditingCategory(null);
    setDialogOpen(true);
  };

  const handleEditClick = (category: Category) => {
    setEditingCategory(category);
    setDialogOpen(true);
  };

  const handlePrintList = () => {
    const companyHeader = companyInfo ? `
      <div style="text-align:center;border-bottom:2px solid #1976d2;padding-bottom:10px;margin-bottom:15px;">
        ${companyInfo.logoPath ? `<img src="${config.API_ORIGIN}${companyInfo.logoPath}" style="max-height:50px;margin-bottom:5px;" onerror="this.style.display='none'" />` : ''}
        <h2 style="color:#1976d2;margin:5px 0;">${isRTL ? companyInfo.companyNameAr : companyInfo.companyName}</h2>
        <p style="font-size:11px;margin:0;color:#666;">
          ${companyInfo.address ? companyInfo.address + ' | ' : ''}${companyInfo.phone ? companyInfo.phone + ' | ' : ''}${companyInfo.email || ''}${companyInfo.website ? ' | ' + companyInfo.website : ''}
        </p>
      </div>
    ` : '';

    const rows = filteredCategories.map(cat => `
      <tr>
        <td>${cat.categoryCode || '-'}</td>
        <td>${cat.categoryName}</td>
        <td>${cat.categoryNameAr}</td>
        <td>${cat.isActive ? (isRTL ? 'نشط' : 'Active') : (isRTL ? 'غير نشط' : 'Inactive')}</td>
      </tr>
    `).join('');

    const html = `<!DOCTYPE html><html dir="${isRTL ? 'rtl' : 'ltr'}"><head>
      <style>
        body{font-family:${isRTL ? 'Cairo,' : ''}Arial,sans-serif;margin:20px;font-size:12px;color:#333;}
        h1{text-align:center;color:#1976d2;margin:10px 0 15px;font-size:18px;}
        table{width:100%;border-collapse:collapse;margin-top:10px;}
        th,td{border:1px solid #ddd;padding:6px 8px;font-size:11px;}
        th{background:#1976d2;color:white;font-weight:bold;}
        tr:nth-child(even){background:#f9f9f9;}
        @page{size:A4 landscape;margin:1cm;}
      </style></head><body>
      ${companyHeader}
      <h1>${t.title}</h1>
      <table>
        <thead><tr>
          <th>${t.code}</th>
          <th>${t.name}</th>
          <th>${t.nameAr}</th>
          <th>${t.status}</th>
        </tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <p style="margin-top:10px;font-size:11px;color:#999;">Printed: ${new Date().toLocaleString()}</p>
    </body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (w) { w.document.write(html); w.document.close(); w.focus(); setTimeout(() => w.print(), 500); }
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      let currentRow = 1;

      // Company Logo
      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({ base64: companyInfo.logoBase64, extension: 'png' });
          worksheet.addImage(logoImage, { tl: { col: 0, row: 0 }, ext: { width: 80, height: 60 } });
        } catch {}
      }

      // Company Header
      worksheet.mergeCells('A1:D1');
      const companyCell = worksheet.getCell('A1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 16, bold: true, color: { argb: 'FF1976D2' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(1).height = 50;
      currentRow = 3;

      // Column Headers
      const headers = [t.code, t.name, t.nameAr, t.status];
      const headerRow = worksheet.getRow(currentRow);
      headerRow.values = headers;
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
      headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1976D2' } };
      headerRow.alignment = { horizontal: 'center', vertical: 'middle' };
      headerRow.height = 25;
      currentRow++;

      // Data rows
      filteredCategories.forEach((cat, idx) => {
        const row = worksheet.getRow(currentRow);
        row.values = [
          cat.categoryCode || '',
          cat.categoryName,
          cat.categoryNameAr,
          cat.isActive ? (isRTL ? 'نشط' : 'Active') : (isRTL ? 'غير نشط' : 'Inactive'),
        ];
        row.alignment = { horizontal: 'center', vertical: 'middle' };
        row.height = 20;
        if (idx % 2 === 0) {
          row.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF5F5F5' } };
        }
        currentRow++;
      });

      // Column widths
      worksheet.getColumn(1).width = 15;
      worksheet.getColumn(2).width = 30;
      worksheet.getColumn(3).width = 30;
      worksheet.getColumn(4).width = 15;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Categories_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      toast.success(t.exportExcel + ' ✓');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error(isRTL ? 'فشل تصدير Excel' : 'Excel export failed');
    }
  };

  return (
    <Box sx={{ direction: isRTL ? 'rtl' : 'ltr' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
            {t.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {t.total}: {categories.length}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={t.printList}>
            <IconButton
              color="primary"
              onClick={handlePrintList}
              sx={{ bgcolor: 'rgba(102, 126, 234, 0.1)', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.2)' } }}
            >
              <PrintIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.exportExcel}>
            <IconButton
              color="success"
              onClick={exportToExcel}
              sx={{ bgcolor: 'rgba(46, 125, 50, 0.1)', '&:hover': { bgcolor: 'rgba(46, 125, 50, 0.2)' } }}
            >
              <ExcelIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Refresh">
            <IconButton onClick={loadCategories} color="primary"><RefreshIcon /></IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddClick}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
              },
            }}
          >
            {t.addCategory}
          </Button>
        </Box>
      </Box>

      <Paper
        elevation={4}
        sx={{
          p: { xs: 2, md: 3 },
          borderRadius: 3,
          background: mode === 'dark'
            ? 'linear-gradient(135deg, #2a2a2a 0%, #1e1e1e 100%)'
            : 'linear-gradient(135deg, #ffffff 0%, #f5f5f5 100%)',
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
          <TextField
            placeholder={t.search}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />

          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, newMode) => newMode && setViewMode(newMode)}
            size="small"
          >
            <ToggleButton value="tree">
              <AccountTree sx={{ mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0 }} />
              {t.treeView}
            </ToggleButton>
            <ToggleButton value="list">
              <ViewList sx={{ mr: isRTL ? 0 : 1, ml: isRTL ? 1 : 0 }} />
              {t.listView}
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 5 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>{t.loading}</Typography>
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : filteredCategories.length === 0 ? (
          <Typography textAlign="center" color="text.secondary" py={5}>
            {t.noCategories}
          </Typography>
        ) : (
          <CategoryTree
            categories={viewMode === 'tree' ? treeCategories : filteredCategories.map(c => ({ ...c, children: [] }))}
            onEdit={handleEditClick}
            onDelete={handleDelete}
          />
        )}
      </Paper>

      <CategoryDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        editCategory={editingCategory}
        allCategories={categories}
      />
    </Box>
  );
};

export default CategoriesPage;